import { DialogEventEnum } from 'app/ui/dialog/dialog-event.enum';
import { Component } from '@angular/core';
import { ApplicationsService } from '../applications.service';
import { UIDialogService } from 'app/ui/dialog/dialog.service';
import { UserSettingsService } from 'app/user/user-settings.service';
import { Application, Binding } from '../application.model';
import { camelCase } from 'app/core/case-transform';
import { mergeMap } from 'rxjs/operators';
import { NgForm } from '@angular/forms';
import { validate } from 'app/user/form.functions';
import { settings, Settings } from 'app/core/settings';

interface CreationModel {
  tenant: string;
  login: string;
  password: string;
  name: string;
  nameChanged: boolean;
  projectsIsLoaded: boolean;
  projects: string[];
  selectedProject: string;
}

@Component({
  selector: 'create-application-dc',
  templateUrl: 'create-application-dc.component.html',
  styleUrls: ['create-application-dc.component.scss']
})
export class CreateApplicationDcComponent {
  creationModel: CreationModel = {
    tenant: '',
    login: '',
    password: '',
    name: null,
    nameChanged: false,
    projectsIsLoaded: false,
    projects: [],
    selectedProject: null
  };
  private iApplicationName: number = 1;
  error: string;
  errorDescription: string;
  permissionsErrorMessage = `The specified account doesn\'t have required permissions<br/> in the tenant.
  <a href="https://abbyy.cloud/media/1133/abbyy_fc_cloud_rest_api_getstarted_10.pdf" target="_blank">Learn more</a>`;

  isLoading = false;

  constructor(
    private applicationsService: ApplicationsService,
    private dialogService: UIDialogService,
    private userSettingsService: UserSettingsService
  ) {}

  get settings(): Settings {
    return settings;
  }

  get urlCloud() {
    return `${this.settings.сloudSite.protocol}://${this.settings.сloudSite.name}`;
  }

  getProjects() {
    this.isLoading = true;
    this.applicationsService
      .getProjects(
        this.creationModel.tenant,
        this.creationModel.login,
        this.creationModel.password
      )
      .subscribe(
        (items: string[]) => {
          if (!items) {
            this.error = 'Error';
            this.errorDescription = this.permissionsErrorMessage;
          } else {
            this.creationModel.projects = items;
            this.creationModel.projectsIsLoaded = true;
          }
          this.isLoading = false;
        },
        error => this._onError(error)
      );
  }

  validate(form: NgForm) {
    validate(form);
  }

  submitForm(form: NgForm) {
    this.validate(form);
    if (this.creationModel.projectsIsLoaded) {
      this._create(form);
    } else {
      this._nextStage(form);
    }
  }

  cancel() {
    this.dialogService.close();
  }

  onHideError(): void {
    this.error = null;
  }

  private async _nextStage(form: NgForm) {
    this.creationModel.projectsIsLoaded = false;

    if (form.invalid) {
      return;
    }

    try {
      await this.getProjects();
    } catch (error) {
      this._onError(error);
    }
  }

  private async _create(form: NgForm) {
    const binding: Binding = {
      id: null,
      tenant: this.creationModel.tenant,
      project: this.creationModel.selectedProject,
      user: this.creationModel.login,
      password: this.creationModel.password
    };

    if (form.invalid) {
      return;
    }

    this.isLoading = true;
    try {
      await this.applicationsService
        .createApplication(
          this.creationModel.name,
          this.userSettingsService.serviceType
        )
        .pipe(
          mergeMap((application: Application) => {
            binding.id = application.id;
            return this.applicationsService.bindingApplication(binding);
          })
        )
        .subscribe(
          () => {
            this.isLoading = false;
            this.dialogService.close(DialogEventEnum.save);
          },
          error => this._onError(error)
        );
    } catch (error) {
      this._onError(error);
    }
  }

  changeProject(nameProject: string): void {
    this.iApplicationName = 2;
    this.setNewNameApplication(nameProject);
    this.onHideError();
  }

  changeName(nameProject: string): void {
    if (!nameProject) {
      this.creationModel.nameChanged = false;
    } else {
      this.creationModel.nameChanged = true;
    }
    this.onHideError();
  }

  private setNewNameApplication(name: string): void {
    const nameApplication = `${name}_${this.iApplicationName}`;
    if (!this.creationModel.nameChanged) {
      if (this.applicationsService.searchByName(name).length === 0) {
        this.creationModel.name = name;
      } else if (
        this.applicationsService.searchByName(nameApplication).length === 0
      ) {
        this.creationModel.name = nameApplication;
      } else {
        this.iApplicationName++;
        this.setNewNameApplication(name);
      }
    }
  }

  private _onError(response): void {
    if (response.status === 400) {
      const responseJson = response.json();
      this.error = camelCase(responseJson.error);
      this.errorDescription = this._getErrorDescription(responseJson);
    }
    this.isLoading = false;
  }

  private _getErrorDescription(responseJson): string {
    const modelState = responseJson.modelState;
    if (!modelState) {
      if (
        responseJson.errorDescription.indexOf(
          'User does not have required role'
        ) === 0
      ) {
        return this.permissionsErrorMessage;
      }
      return responseJson.errorDescription;
    }
    return Object.keys(modelState)
      .map(fieldName => modelState[fieldName].join('\n'))
      .join('\n');
  }
}
